/**
 * Stack-aware navigation helper.
 *
 * Use this in place of raw `navigate(-1)` on pages that can be accessed
 * directly (login, register, share landing, deep links). If there is a
 * real previous entry in the tab history it pops, otherwise it replaces
 * to the given fallback so the user never gets stuck on an orphan page.
 */
import { useNavigate } from "react-router-dom";

export type SafeBackFallback = "/" | "/login";

export function useSafeBack(fallback: SafeBackFallback = "/") {
  const navigate = useNavigate();

  return () => {
    if (typeof window === "undefined") {
      navigate(fallback, { replace: true });
      return;
    }

    // PWA cold-start: history.length === 1 (current entry only).
    // In-app navigations always grow it.
    const hasHistory = window.history.length > 1;

    if (hasHistory) {
      navigate(-1);
      return;
    }

    navigate(fallback, { replace: true });
  };
}
